/**
 * ShareUserIdentityBlock — the main peer-info bubble of a share-user bubble
 * (`ShareUserBubble`): the shared user's circular avatar next to a column with
 * their display name and, when present, their `@username`. Extracted from
 * `ShareUserBubble` to keep that file under the component-size threshold.
 *
 * Same content on the own and the peer variant; only the surrounding row order
 * and the corner-collapse direction differ, and both of those are owned by the
 * parent.
 *
 * Non-obvious behaviour:
 *  - The username row is hidden entirely when `username` is null/empty. When
 *    present, the rendered text is prefixed with `@` (the stored field never
 *    includes it). Mirrors Flutter `chat_screen.dart:2025-2027` / `:3127-3129`
 *    after the matching parity fix.
 *  - Background is `theme.chat.peerMessage` on BOTH variants —
 *    `chat_screen.dart:1977` (own) / `:3079` (peer). Flutter's
 *    `ownMessageColor` is reserved for the optional reply-text bubble below,
 *    which is `ShareCaptionBubble`'s business.
 *  - Renders defensively when `shareInfo` is missing: every field falls back to
 *    an empty string rather than crashing (`shareInfo == null` should not
 *    happen for a real shareUser message — the parent's tap handler
 *    early-returns in that case).
 *
 * Deviations from plan:
 *  - Profile circle uses `responsive.avatar.large` (= 56) instead of the
 *    Flutter `shareChatProfileIconSize` (= 60); 56 is the closest token on the
 *    project's `avatar.*` scale. Small UI deviation per `presentation.md`.
 *
 * No QA handle of its own — the parent's wrapper handle
 * (`chat-bubble-share-user-<documentId>`) already spans this block, and the
 * caption's own handle lives on `ShareCaptionBubble`.
 */

import React from 'react';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { BubbleCornerRadii } from '@/domain/utils/chatUtils';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';

interface ShareUserIdentityBlockProps {
  /** The share-user message whose `shareInfo.usersInfo` is rendered. */
  message: ChatMessageEntity;
  /**
   * Corner radii resolved once by the parent via
   * `ChatUtils.getBubbleCornerRadii`.
   */
  corners: BubbleCornerRadii;
}

export const ShareUserIdentityBlock: React.FC<ShareUserIdentityBlockProps> = ({
  message,
  corners,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const shareInfo = message.shareInfo;
  const sharedUserId = shareInfo?.usersInfo.userId ?? '';
  const sharedName = shareInfo?.usersInfo.name ?? '';
  // Render the stored `username` prefixed with `@` when present; otherwise
  // skip the row entirely. Mirrors Flutter `chat_screen.dart:2025-2027` /
  // `:3127-3129` after the matching parity fix that drops the
  // `"@testuser_user"` fallback. The `@` is rendering-only — the stored
  // `username` field never includes it.
  const usernameValue = shareInfo?.usersInfo.username ?? '';
  const hasUsername = usernameValue.length > 0;

  return (
    <div
      style={{
        backgroundColor: theme.chat.peerMessage,
        borderTopLeftRadius: corners.topLeft,
        borderTopRightRadius: corners.topRight,
        borderBottomLeftRadius: corners.bottomLeft,
        borderBottomRightRadius: corners.bottomRight,
        paddingTop: responsive.spacing.sm,
        paddingBottom: responsive.spacing.sm,
        paddingLeft: responsive.spacing.sm,
        paddingRight: responsive.spacing.sm,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: responsive.spacing.sm,
        maxWidth: '100%',
      }}
    >
      <CachedImage
        src={
          sharedUserId ? UserUtils.generateUserProfileImageUrl(sharedUserId) : ''
        }
        alt={sharedName}
        style={{
          width: responsive.avatar.large,
          height: responsive.avatar.large,
          borderRadius: responsive.borderRadius.round,
          // Flutter's `CircleBorderAvatar` defaults `borderColor` to
          // `AppColors.transparent` and `borderWidth` to `0`
          // (`circle_border_avatar.dart:25-29`), and the `chat_screen.dart`
          // share-user call sites (`:2003` own / `:3106` peer) only pass
          // `key`, `size`, and `image`. So no ring is drawn in Flutter.
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          minWidth: 0,
        }}
      >
        <span
          style={{
            color: theme.white,
            fontSize: responsive.font.lg,
            fontWeight: responsive.fontWeight.regular,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {sharedName}
        </span>
        {hasUsername ? (
          <span
            style={{
              color: theme.text.primary,
              fontSize: responsive.font.base,
              fontWeight: responsive.fontWeight.regular,
              marginTop: responsive.spacing.xxs,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {`@${usernameValue}`}
          </span>
        ) : null}
      </div>
    </div>
  );
};
